/**
 * Agent 管理 API
 *
 * 提供 Agent 注册、查询、调用关系管理等功能。
 * 支持多 Agent 协作，实现 AI Native 产品理念。
 */

import apiClient from './index'
import { AgentSchema, AgentListResponseSchema } from '@/schemas/agents'
import { logger } from '@/utils/logger'

// ============== 类型定义 ==============

/**
 * Agent 基础类型
 */
export interface Agent {
  id: string
  name: string
  role: string | null
  description: string | null
  icon: string | null
  color: string | null
  avatar_url: string | null
  agent_type: string
  tags: string[] | null
  endpoint: string | null
  invoke_method: string
  input_schema: Record<string, unknown> | null
  output_schema: Record<string, unknown> | null
  capabilities: string[] | null
  status: 'active' | 'inactive' | 'deprecated'
  is_system: boolean
  is_callable: boolean
  timeout_seconds: number
  max_retries: number
  call_count: number
  success_count: number
  success_rate: number
  avg_latency_ms: number | null
  version: string
  config: Record<string, unknown> | null
  brand_version?: number
  brand_published_at?: string | null
  brand_draft?: Record<string, string> | null
  // 技能声明 JSONB：bound_skill_ids=页面绑定的 skill，skills=外部 /info 拉取（互不覆盖）
  skills_declaration?: Record<string, unknown> | null
  // ====== 治理口径显式契约（PR5 / Phase 3）======
  // 后端从 config 派生回传，前端直接读这些稳定字段，不再解析裸 config。
  // 详见 docs/architecture/AGENT_DB_SOURCE_OF_TRUTH_AUDIT_2026-06-03.md §10.6
  business: boolean
  surface_type: 'business' | 'skill' | 'internal' | 'fallback' | 'deprecated' | null
  parent_agent_id: string | null
  list_visible: boolean | null
  workspace_visible: boolean | null
  canonical_id: string | null
  replaced_by: string | null
  created_at: string
  updated_at: string
}

/**
 * Agent 列表响应
 */
export interface AgentListResponse {
  agents: Agent[]
  total: number
}

/**
 * 创建 Agent 请求
 */
export interface AgentCreateRequest {
  id?: string
  name: string
  description?: string
  icon?: string
  color?: string
  agent_type?: string
  tags?: string[]
  endpoint?: string
  invoke_method?: string
  input_schema?: Record<string, unknown>
  output_schema?: Record<string, unknown>
  capabilities?: string[]
  is_callable?: boolean
  timeout_seconds?: number
  max_retries?: number
  config?: Record<string, unknown>
}

/**
 * 更新 Agent 请求
 */
export interface AgentUpdateRequest {
  name?: string
  description?: string | null
  icon?: string
  color?: string
  agent_type?: string
  tags?: string[]
  endpoint?: string
  invoke_method?: string
  input_schema?: Record<string, unknown>
  output_schema?: Record<string, unknown>
  capabilities?: string[]
  status?: 'active' | 'inactive' | 'deprecated'
  is_callable?: boolean
  timeout_seconds?: number
  max_retries?: number
  config?: Record<string, unknown>
  // 技能绑定声明（读-改-写整体回传，bound_skill_ids 为页面绑定的 skill）
  skills_declaration?: Record<string, unknown>
}

/**
 * 人设块类型
 * 用于构建 Agent 的系统提示词，支持角色/背景/技能/任务等多个维度
 */
export interface PersonaBlock {
  key: string // 块标识（如 role, background, skills, task）
  title: string // 显示标题（如 "角色", "背景"）
  content: string // 块内容
  weight: number // 权重（0-1），用于控制重要性
}

/**
 * 变量类型
 * 用于在 Prompt 模板中定义可替换的占位符
 */
export interface PromptVariable {
  name: string // 变量名（如 user_id, date_range）
  type: 'string' | 'number' | 'date' // 变量类型
  defaultValue: string // 默认值
  description?: string // 变量描述（可选）
}

/**
 * Agent 闲聊配置
 * 对标 Dify/Coze/GPTs 等行业标准
 */
export interface ChatConfig {
  system_prompt?: string // 系统提示词（可选，覆盖默认）
  opening_line?: string // 开场白内容
  opening_line_enabled?: boolean // 是否启用开场白
  suggest_questions?: string[] // 推荐问题列表
  suggest_mode?: 'manual' | 'ai_auto' // 推荐问题模式
  persona_blocks?: PersonaBlock[] // 人设块（角色/背景/技能/任务等）
  variables?: PromptVariable[] // 模板变量
}

/**
 * Agent 模型配置请求
 */
export interface ModelConfigUpdateRequest {
  model_id?: string
  provider?: string
  temperature?: number
  max_tokens?: number
}

/**
 * Agent 工具配置项
 */
export interface AgentToolConfig {
  name: string // 工具名称（唯一标识）
  type: string // 工具类型：builtin/mcp/http/agent
  description: string // 工具描述
  enabled: boolean // 是否启用
  config?: Record<string, unknown> // 工具额外配置
}

/**
 * 记忆遗忘策略配置
 */
export interface MemoryRetentionConfig {
  preferences: string // 用户偏好保留策略：forever/90d/30d/7d
  sessions: string // 会话摘要保留策略：forever/90d/30d/7d
  queryPatternsLimit: number // 查询模式最大保留条数
  errorLessonsLimit: number // 错误教训最大保留条数
}

/**
 * 记忆配置
 * 控制 Agent 的长期记忆行为，包括记忆类型启停和遗忘策略
 */
export interface MemoryConfig {
  enabled: boolean // 是否启用长期记忆
  types: Record<string, boolean> // 各记忆类型的启用状态
  retention: MemoryRetentionConfig // 遗忘策略
  maxMemories: number // 最大记忆数量
  relevanceThreshold: number // 相关性阈值
  sessionSummaryTTL: number // 会话摘要 TTL（天）
  archiveAfterDays: number // 归档天数
}

/**
 * 更新 Agent 闲聊配置请求
 */
export interface ChatConfigUpdateRequest {
  agent_name?: string
  agent_role?: string
  system_prompt?: string
  opening_line?: string
  opening_line_enabled?: boolean
  suggest_questions?: string[]
  suggest_mode?: 'manual' | 'ai_auto'
  persona_blocks?: PersonaBlock[]
  variables?: PromptVariable[]
  model_settings?: ModelConfigUpdateRequest
  tools_settings?: AgentToolConfig[]
  memory_settings?: MemoryConfig
  /** 标签（含游戏品类等） */
  tags?: string[]
}

/**
 * 获取 Agent 闲聊配置响应
 */
export interface ChatConfigResponse {
  agent_id: string
  agent_name: string
  agent_role: string | null
  capabilities: string[] | null
  chat_config: ChatConfig
  model_config?: Record<string, unknown>
  tools_config?: AgentToolConfig[]
  memory_config?: MemoryConfig
}

/**
 * 协作模式枚举
 */
export type CollaborationMode = 'sequential' | 'parallel' | 'conditional' | 'hierarchical' | 'loop'

/**
 * 触发类型枚举
 */
export type TriggerType = 'always' | 'intent' | 'keyword' | 'condition' | 'manual'

/**
 * 上下文策略枚举
 */
export type ContextPolicy = 'full' | 'summary' | 'selective' | 'none'

/**
 * 聚合策略枚举
 */
export type AggregationStrategy = 'merge' | 'first_success' | 'vote' | 'best_score' | 'concat'

/**
 * Agent 调用关系（增强版，支持协作模式）
 */
export interface AgentCallableRelation {
  id: number
  caller_agent_id: string
  callee_agent_id: string
  callee_agent_name: string | null
  callee_agent_icon: string | null
  callee_agent_color: string | null
  callee_agent_description: string | null
  enabled: boolean
  priority: number

  // 协作模式
  collaboration_mode: CollaborationMode

  // 触发配置
  trigger_type: TriggerType
  trigger_condition: Record<string, unknown> | null
  condition_expr: string | null

  // 上下文配置
  context_policy: ContextPolicy
  context_fields: string[] | null

  // 执行配置
  timeout_seconds: number
  max_retries: number
  is_async: boolean

  // 循环配置
  max_iterations: number
  exit_condition: string | null

  // 并行配置
  aggregation_strategy: AggregationStrategy
  group_id: string | null

  // 描述
  description: string | null

  // 额外配置
  config: Record<string, unknown> | null

  created_at: string
  updated_at: string
}

/**
 * 可调用 Agent 列表响应
 */
export interface CallableAgentsResponse {
  caller_agent_id: string
  callable_agents: AgentCallableRelation[]
  total: number
}

/**
 * 调用关系请求（增强版，支持协作配置）
 */
export interface CallableRelationRequest {
  callee_agent_id: string
  enabled?: boolean
  priority?: number

  // 协作模式
  collaboration_mode?: CollaborationMode

  // 触发配置
  trigger_type?: TriggerType
  trigger_condition?: Record<string, unknown>
  condition_expr?: string

  // 上下文配置
  context_policy?: ContextPolicy
  context_fields?: string[]

  // 执行配置
  timeout_seconds?: number
  max_retries?: number
  is_async?: boolean

  // 循环配置
  max_iterations?: number
  exit_condition?: string

  // 并行配置
  aggregation_strategy?: AggregationStrategy
  group_id?: string

  // 描述
  description?: string

  // 额外配置
  config?: Record<string, unknown>
}

/**
 * Agent 调用请求
 */
export interface AgentInvokeRequest {
  query: string
  context?: Record<string, unknown>
  trace_id?: string
}

/**
 * Agent 调用响应
 */
export interface AgentInvokeResponse {
  success: boolean
  data: Record<string, unknown> | null
  error: string | null
  trace_id: string
  latency_ms: number | null
}

/**
 * Agent 类型选项（用于下拉框）
 */
export interface AgentTypeOption {
  key: string
  name: string
  type: string
  order: number
}

/**
 * 协作选项（下拉框选项）
 */
export interface CollaborationOption {
  value: string
  label: string
  description: string
}

/**
 * 协作选项响应
 */
export interface CollaborationOptionsResponse {
  collaboration_modes: CollaborationOption[]
  trigger_types: CollaborationOption[]
  context_policies: CollaborationOption[]
  aggregation_strategies: CollaborationOption[]
}

/**
 * 协作编排请求
 */
export interface OrchestrationRequest {
  query: string
  caller_agent_id: string
  session_id?: string
  user_id?: string
  context?: Record<string, unknown>
  target_agent_ids?: string[]
}

/**
 * 协作编排响应
 */
export interface OrchestrationResponse {
  success: boolean
  trace_id: string
  execution_path: string[]
  results: Record<string, unknown>
  final_result: Record<string, unknown> | null
  errors: Array<{ agent_id: string; error: string; error_type: string; timestamp: string }>
  latency_ms: number
}

/**
 * Agent 分析统计摘要（来自 analytics API）
 */
export interface AgentAnalyticsSummary {
  summary: {
    total_calls: number
    success_rate: number
    avg_latency_ms: number
    active_users: number
    total_tokens: number
    estimated_cost: number
    calls_change: number
    success_rate_change: number
    latency_change: number
    users_change: number
  }
  call_trend: Array<{ date: string; total: number; success: number; error: number }>
  latency_distribution: Array<{ bucket: string; count: number; percentage: number }>
  node_latency_top: Array<{ node: string; avg_latency_ms: number; call_count: number }>
  error_categories: Array<{ category: string; count: number; percentage: number }>
  token_trend: Array<{ date: string; prompt_tokens: number; completion_tokens: number }>
  satisfaction: {
    positive_rate: number
    negative_reasons: Array<{ reason: string; percentage: number }>
  }
}

/**
 * Agent 统计摘要（简化版，用于列表展示）
 */
export interface AgentStatsSummary {
  callCount: number
  successRate: number
  avgLatencyMs: number
}

// ============== API 实现 ==============

/**
 * Agent 管理 API
 */
export const agentsApi = {
  /**
   * 获取所有 Agent 列表
   */
  async listAgents(params?: {
    agent_type?: string
    status?: string
    is_callable?: boolean
    search?: string
  }): Promise<AgentListResponse> {
    // 响应拦截器已统一解包 ApiResponse.data，Zod 校验捕获结构变化
    const raw = await apiClient.get<AgentListResponse>('/agents', { params })
    return AgentListResponseSchema.parse(raw)
  },

  /**
   * 获取单个 Agent 详情
   */
  async getAgent(agentId: string): Promise<Agent> {
    const raw = await apiClient.get<Agent>(`/agents/${agentId}`)
    return AgentSchema.parse(raw)
  },

  /**
   * 创建新 Agent
   */
  async createAgent(data: AgentCreateRequest): Promise<Agent> {
    const raw = await apiClient.post<Agent>('/agents', data)
    return AgentSchema.parse(raw)
  },

  /**
   * 更新 Agent
   */
  async updateAgent(agentId: string, data: AgentUpdateRequest): Promise<Agent> {
    return apiClient.put<Agent>(`/agents/${agentId}`, data)
  },

  /**
   * 删除 Agent
   */
  async deleteAgent(agentId: string): Promise<void> {
    await apiClient.delete(`/agents/${agentId}`)
  },

  /**
   * 获取 Agent 可调用的其他 Agent 列表
   */
  async getCallableAgents(agentId: string): Promise<CallableAgentsResponse> {
    return apiClient.get<CallableAgentsResponse>(`/agents/${agentId}/callables`)
  },

  /**
   * 更新 Agent 可调用关系
   */
  async updateCallableAgents(
    agentId: string,
    relations: CallableRelationRequest[]
  ): Promise<{ success: boolean; updated: number }> {
    return apiClient.put<{ success: boolean; updated: number }>(
      `/agents/${agentId}/callables`,
      relations
    )
  },

  /**
   * 切换单个调用关系的启用状态
   */
  async toggleCallableRelation(
    callerId: string,
    calleeId: string,
    enabled: boolean
  ): Promise<{ success: boolean; enabled: boolean }> {
    return apiClient.post<{ success: boolean; enabled: boolean }>(
      `/agents/${callerId}/callables/${calleeId}/toggle`,
      null,
      { params: { enabled } }
    )
  },

  /**
   * Agent 调用另一个 Agent
   */
  async invokeAgent(
    callerId: string,
    calleeId: string,
    request: AgentInvokeRequest
  ): Promise<AgentInvokeResponse> {
    return apiClient.post<AgentInvokeResponse>(`/agents/${callerId}/invoke/${calleeId}`, request)
  },

  /**
   * 获取 Agent 类型选项（用于提示词管理的下拉框）
   */
  async getAgentTypeOptions(): Promise<AgentTypeOption[]> {
    const response = await this.listAgents({ status: 'active' })
    return response.agents.map((agent, index) => ({
      key: agent.id,
      name: agent.name,
      type: agent.agent_type,
      order: index + 1
    }))
  },

  /**
   * 初始化默认 Agent（内部使用）
   */
  async initDefaultAgents(): Promise<{ success: boolean; initialized: number }> {
    return apiClient.post<{ success: boolean; initialized: number }>('/agents/init')
  },

  /**
   * 更新单个调用关系的协作配置
   */
  async updateCallableRelation(
    callerId: string,
    calleeId: string,
    config: CallableRelationRequest
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.put<{ success: boolean; message: string }>(
      `/agents/${callerId}/callables/${calleeId}`,
      config
    )
  },

  /**
   * 获取协作模式相关的枚举选项
   */
  async getCollaborationOptions(): Promise<CollaborationOptionsResponse> {
    return apiClient.get<CollaborationOptionsResponse>('/agents/collaboration-options')
  },

  /**
   * 执行多 Agent 协作编排
   */
  async orchestrate(request: OrchestrationRequest): Promise<OrchestrationResponse> {
    return apiClient.post<OrchestrationResponse>('/agents/orchestrate', request)
  },

  /**
   * 获取 Agent 闲聊配置
   *
   * @param agentId Agent ID
   * @returns 闲聊配置，包含 system_prompt, opening_line, persona_blocks 等
   */
  async getChatConfig(agentId: string): Promise<ChatConfigResponse> {
    return apiClient.get<ChatConfigResponse>(`/agents/${agentId}/chat-config`)
  },

  /**
   * 更新 Agent 闲聊配置（部分更新）
   *
   * 对标 Dify/Coze/GPTs 等行业标准的 Agent 配置能力：
   * - system_prompt: 系统提示词
   * - opening_line: 开场白
   * - suggest_questions: 推荐问题
   * - persona_blocks: 人设块（角色/背景/技能/任务等）
   *
   * @param agentId Agent ID
   * @param config 闲聊配置更新请求
   * @returns 更新后的 Agent
   */
  async updateChatConfig(agentId: string, config: ChatConfigUpdateRequest): Promise<Agent> {
    return apiClient.patch<Agent>(`/agents/${agentId}/chat-config`, config)
  },

  /**
   * 获取 Agent 分析统计数据
   *
   * @param agentId Agent ID
   * @param period 时间范围: today/7d/30d
   * @returns 分析统计数据
   */
  async getAgentAnalytics(agentId: string, period: string = '30d'): Promise<AgentAnalyticsSummary> {
    return apiClient.get<AgentAnalyticsSummary>(`/agents/${agentId}/analytics`, {
      params: { period }
    })
  },

  /**
   * 批量获取多个 Agent 的统计摘要
   *
   * @param agentIds Agent ID 列表
   * @param period 时间范围
   * @returns Agent ID 到统计数据的映射
   */
  /**
   * 生成 Agent AI 头像
   *
   * 调用后端生图 + 裁切 + OSS 上传，返回头像 URL。
   * 生成后自动保存到 Agent 的 avatar_url 字段。
   */
  async generateAvatar(
    agentId: string,
    style: 'pixar' | 'realistic' | 'anime' = 'pixar'
  ): Promise<{ avatar_url: string; agent_id: string }> {
    return apiClient.post<{ avatar_url: string; agent_id: string }>(
      `/agents/${agentId}/avatar/generate`,
      { style }
    )
  },

  /** 品牌编辑 — 写入 brand_draft，不改主字段 */
  async updateBrand(agentId: string, data: Record<string, string>): Promise<unknown> {
    return apiClient.put(`/agents/${agentId}/brand`, data)
  },

  /** 品牌发布 — brand_draft 写入主字段 */
  async publishBrand(agentId: string): Promise<unknown> {
    return apiClient.post(`/agents/${agentId}/brand/publish`)
  },

  /** 品牌版本历史 */
  async getBrandHistory(agentId: string, params?: { page?: number; page_size?: number }): Promise<unknown> {
    return apiClient.get(`/agents/${agentId}/brand/history`, { params })
  },

  async batchGetAgentStats(
    agentIds: string[],
    period: string = '30d'
  ): Promise<Record<string, AgentStatsSummary>> {
    // 并发获取每个 Agent 的统计数据
    const results: Record<string, AgentStatsSummary> = {}
    const promises = agentIds.map(async agentId => {
      try {
        const analytics = await this.getAgentAnalytics(agentId, period)
        results[agentId] = {
          callCount: analytics.summary?.total_calls || 0,
          successRate: analytics.summary?.success_rate || 0,
          avgLatencyMs: analytics.summary?.avg_latency_ms || 0
        }
      } catch (error) {
        // 如果获取失败，使用默认值
        logger.warn(`获取 Agent ${agentId} 统计数据失败:`, error)
        results[agentId] = { callCount: 0, successRate: 0, avgLatencyMs: 0 }
      }
    })
    await Promise.all(promises)
    return results
  }
}

// ============== Pipeline API ==============

/**
 * Pipeline 节点元数据
 */
export interface PipelineNode {
  id: string
  display_name: string
  description: string
  node_type: string
  prompts: string[]
  order: number
  branch: string | null
  is_conditional: boolean
}

/**
 * Pipeline 边元数据
 */
export interface PipelineEdge {
  from: string
  to: string
  condition?: string
  branch?: string
}

/**
 * Pipeline 分支元数据
 */
export interface PipelineBranch {
  name: string
  color: string
  description: string
}

/**
 * Pipeline 图定义
 */
export interface PipelineDefinition {
  nodes: PipelineNode[]
  edges: PipelineEdge[]
  branches: Record<string, PipelineBranch>
}

/**
 * 获取 Agent 的执行流水线定义
 */
export async function getAgentPipeline(agentId: string): Promise<PipelineDefinition> {
  return apiClient.get<PipelineDefinition>(`/agents/${agentId}/pipeline`)
}

/**
 * Pipeline 节点摘要（用于 Prompt 工程页面节点分组）
 */
export interface PipelineNodeSummary {
  id: string
  display_name: string
  description: string
  node_type: string
  order: number
}

/**
 * 全部 Pipeline 响应
 */
export interface AllPipelinesResponse {
  pipelines: Record<
    string,
    {
      nodes: PipelineNodeSummary[]
      description: string
    }
  >
}

/**
 * 获取所有 Agent 的 Pipeline 定义（含节点元数据）
 */
export async function getAllPipelines(): Promise<AllPipelinesResponse> {
  return apiClient.get<AllPipelinesResponse>('/llmops/prompts/meta/pipelines')
}

// ============== 兼容旧 API ==============

// 为了兼容现有代码，保留旧的导出方式
export default agentsApi
